/**
 * EbdDiagram.tsx — SVG renderer for a laid-out EBD graph.
 *
 * Pure presentation: takes an EbdGraph (see ebdLayout.ts) and draws decision
 * spine, result clusters and edges. Pan = drag, zoom = Ctrl/⌘ + wheel (applied
 * as a CSS transform on the wrapper so the inner <svg> stays at full, clean
 * coordinates — that is exactly what Export-SVG serialises).
 */
import { forwardRef, useCallback, useRef, useState } from "react";
import type { EbdGraph, EbdNode } from "./ebdLayout";

const C = {
  root: { fill: "#9fb0c3", stroke: "#7a8da1", text: "#16202c" },
  decision: { fill: "#c3d0df", stroke: "#7a8da1", text: "#16202c" },
  cluster: { fill: "#dde2d0", stroke: "#9aa882", text: "#2a2f22" },
  end: { fill: "#8fb3a0", stroke: "#669580", text: "#16202c" },
  arrow: "#6f9a7f",
  badge: "#5b7088",
  badgeText: "#ffffff",
};

const PAD_X = 16;
const PAD_Y = 14;
const LINE_H = 20;
const BADGE_H = 24;

function NodeShape({ n }: { n: EbdNode }) {
  const palette = C[n.kind];
  const radius = n.kind === "end" ? n.h / 2 : 12;
  const textTop = n.y + PAD_Y + BADGE_H;
  return (
    <g>
      <rect
        x={n.x}
        y={n.y}
        width={n.w}
        height={n.h}
        rx={radius}
        ry={radius}
        fill={palette.fill}
        stroke={palette.stroke}
        strokeWidth={1.5}
      />

      {n.kind === "end" ? (
        <text
          x={n.x + n.w / 2}
          y={n.y + n.h / 2}
          textAnchor="middle"
          dominantBaseline="central"
          fontWeight={700}
          fontSize={16}
          fill={palette.text}
        >
          Ende
        </text>
      ) : (
        <>
          {/* header badge: step number, result code or EBD code */}
          {n.badge && (
            <g>
              <rect
                x={n.x + PAD_X}
                y={n.y + PAD_Y - 2}
                width={n.kind === "decision" ? 30 : 56}
                height={BADGE_H}
                rx={6}
                ry={6}
                fill={C.badge}
              />
              <text
                x={n.x + PAD_X + (n.kind === "decision" ? 15 : 28)}
                y={n.y + PAD_Y + BADGE_H / 2 - 1}
                textAnchor="middle"
                dominantBaseline="central"
                fontWeight={700}
                fontSize={13}
                fill={C.badgeText}
              >
                {n.badge}
              </text>
            </g>
          )}
          {/* root: ebd_name beside the code */}
          {n.subtitle && (
            <text
              x={n.x + PAD_X + 70}
              y={n.y + PAD_Y + BADGE_H / 2 - 1}
              dominantBaseline="central"
              fontSize={13}
              fontStyle="italic"
              fill={palette.text}
            >
              {n.subtitle}
            </text>
          )}
          {/* body text */}
          <text fill={palette.text} fontSize={14}>
            {n.lines.map((line, i) => (
              <tspan key={i} x={n.x + PAD_X} y={textTop + i * LINE_H + 8}>
                {line}
              </tspan>
            ))}
          </text>
        </>
      )}
    </g>
  );
}

function pointsToPath(points: { x: number; y: number }[]): string {
  return points.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
}

export const EbdDiagram = forwardRef<SVGSVGElement, { graph: EbdGraph }>(
  function EbdDiagram({ graph }, ref) {
    const [zoom, setZoom] = useState(0.85);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const drag = useRef<{ x: number; y: number; ox: number; oy: number } | null>(null);

    const onWheel = useCallback((e: React.WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      setZoom((z) => Math.min(2.5, Math.max(0.2, z * (e.deltaY < 0 ? 1.1 : 0.9))));
    }, []);

    const onPointerDown = (e: React.PointerEvent) => {
      (e.target as Element).setPointerCapture?.(e.pointerId);
      drag.current = { x: e.clientX, y: e.clientY, ox: pan.x, oy: pan.y };
    };
    const onPointerMove = (e: React.PointerEvent) => {
      if (!drag.current) return;
      setPan({
        x: drag.current.ox + (e.clientX - drag.current.x),
        y: drag.current.oy + (e.clientY - drag.current.y),
      });
    };
    const endDrag = () => {
      drag.current = null;
    };

    const zoomBy = (f: number) => setZoom((z) => Math.min(2.5, Math.max(0.2, z * f)));
    const reset = () => {
      setZoom(0.85);
      setPan({ x: 0, y: 0 });
    };

    return (
      <div className="ebd-canvas" onWheel={onWheel}>
        <div className="ebd-zoom-controls">
          <button type="button" onClick={() => zoomBy(1.15)} aria-label="Vergrößern">+</button>
          <button type="button" onClick={() => zoomBy(0.87)} aria-label="Verkleinern">−</button>
          <button type="button" onClick={reset} aria-label="Zurücksetzen">⤢</button>
          <span className="ebd-zoom-pct">{Math.round(zoom * 100)}%</span>
        </div>

        <div
          className="ebd-pan"
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
            cursor: drag.current ? "grabbing" : "grab",
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerLeave={endDrag}
        >
          <svg
            ref={ref}
            width={graph.width}
            height={graph.height}
            viewBox={`0 0 ${graph.width} ${graph.height}`}
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: "block" }}
          >
            <defs>
              <marker
                id="ebd-arrow"
                viewBox="0 0 10 10"
                refX="9"
                refY="5"
                markerWidth="7"
                markerHeight="7"
                orient="auto-start-reverse"
              >
                <path d="M 0 0 L 10 5 L 0 10 z" fill={C.arrow} />
              </marker>
            </defs>

            {/* title block */}
            <text x={56} y={24} fontWeight={700} fontSize={16} fill="#16202c">
              {graph.title}
            </text>
            {graph.subtitle && (
              <text x={56} y={42} fontSize={13} fill="#52606d">
                {graph.subtitle}
              </text>
            )}

            {/* edges under nodes */}
            {graph.edges.map((e) => {
              const mid = e.points[0];
              return (
                <g key={e.id}>
                  <path
                    d={pointsToPath(e.points)}
                    fill="none"
                    stroke={C.arrow}
                    strokeWidth={1.8}
                    markerEnd="url(#ebd-arrow)"
                  />
                  {e.label && (
                    <text
                      x={mid.x + (e.kind === "branch" ? 8 : 8)}
                      y={mid.y + (e.kind === "branch" ? -6 : 14)}
                      fontSize={13}
                      fontWeight={700}
                      fill="#3f5a49"
                    >
                      {e.label.toUpperCase()}
                    </text>
                  )}
                </g>
              );
            })}

            {/* nodes */}
            {graph.nodes.map((n) => (
              <NodeShape key={n.id} n={n} />
            ))}
          </svg>
        </div>

        <div className="ebd-hint">Strg/⌘ + Mausrad zum Zoomen · Ziehen zum Verschieben</div>
      </div>
    );
  },
);
